import React, { useEffect, useRef, useState } from 'react';
import { style } from 'typestyle';
import { px } from 'csx';
import { spacing } from '../../../theme/spacing';
import { rebrand } from '../../../theme/color';
import { Button } from '../atoms/button/Button';
import { ButtonProminence, ButtonSize } from '../atoms/button/buttonStyles';
import throttle from 'lodash/throttle';
import { ArrowDown } from '../atoms/icons/ArrowDown';
import { centerCenter, flexRoot } from 'csstips';

interface TruncatedTextProps {
  content: string;
  maxHeight?: number;
  fadeToColour?: string;
}

const styles = {
  heading: style({
    margin: `0 0 ${spacing.DEFAULT}`,
    fontSize: px(32),
  }),
  contentWrapper: (hideOverflow: boolean, maxHeight: number, fadeToColour: string) =>
    style({
      position: 'relative',
      overflow: 'hidden',
      maxHeight: hideOverflow ? px(maxHeight) : 'none',
      $nest: {
        '&::after': {
          content: "''",
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: px(64),
          width: '100%',
          backgroundImage: `linear-gradient(to bottom, transparent, ${fadeToColour})`,
          display: hideOverflow ? 'block' : 'none',
        },
      },
    }),
  content: style({
    margin: 0,
  }),
  expandBtn: style(flexRoot, centerCenter, {
    width: '100%',
    marginTop: spacing.DEFAULT,
  }),
  icon: (isExpanded: boolean) =>
    style({
      marginLeft: spacing.XSMALL,
      transform: isExpanded ? 'rotate(180deg)' : 'none',
    }),
};

/**
 * Text block with a max-height
 *
 * Shows an expand/collapse button if the text exceeds this height
 */
export const TruncatedText: React.FC<TruncatedTextProps> = ({
  content,
  maxHeight = 200,
  fadeToColour = rebrand.neutralOnDark[10].toString(),
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(true);
  const contentEl = useRef<HTMLParagraphElement>(null);

  // When the component first mounts, check if the content height is greater than the max-height
  // Then run the same check on resize, in case the window width changes the element height
  useEffect(() => {
    checkOverflowValue();

    window.addEventListener('resize', handleResize, false);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleExpanded = (): void => {
    setIsExpanded((currentValue) => !currentValue);
  };

  const checkOverflowValue = (): void => {
    if (contentEl.current) {
      const { height } = contentEl.current.getBoundingClientRect();

      setHasOverflow(height > maxHeight);
    }
  };

  const handleResize = throttle(checkOverflowValue, 300);

  return (
    <div>
      <div className={styles.contentWrapper(!isExpanded && hasOverflow, maxHeight, fadeToColour)}>
        <p ref={contentEl} className={styles.content} dangerouslySetInnerHTML={{ __html: content }}></p>
      </div>

      {hasOverflow && (
        <Button
          className={styles.expandBtn}
          prominence={ButtonProminence.SECONDARY}
          size={ButtonSize.SMALL}
          onClick={toggleExpanded}
        >
          Show {isExpanded ? 'less' : 'more'}
          <ArrowDown isSquare={true} size={7} className={styles.icon(isExpanded)} />
        </Button>
      )}
    </div>
  );
};
